/*

👋 "Hey" from the Praxis team!

'apiConfig' is one pattern that can be used to set environment-based values at build time.
This happens by creating an environment variable REACT_APP_ENV, and then matching corresponding
keys to each environment in the 'envConfigs' object below. To learn more about how to set
REACT_APP_ENV, view the README generated with this app.


For an alternative pattern - using TAP configuration to set environment-based values at cluster initialization - see:
@praxis/component-runtime-env - https://praxis.prod.target.com/components/runtime-env

*/

import { AuthConfig } from '@praxis/component-auth'
import _ from 'lodash'

export interface NotificationApiConfig {
  readonly baseUrl: string
  readonly appName: string
  readonly apiKey: string
  readonly getUrl: string
  readonly statusUpdateUrl: string
  readonly deleteUrl: string
  readonly filterUrl: string
}

export interface NotificationFirebaseConfig {
  readonly vapidKey: string
  readonly apiKey: string
  readonly authDomain: string
  readonly projectId: string
  readonly storageBucket: string
  readonly messagingSenderId: string
  readonly appId: string
}

export interface NotificationAnalyticsConfig {
  readonly appName: string
  readonly apiKey: string
  readonly url: string
}

export interface NotificationConfig {
  readonly apiConfig: NotificationApiConfig
  readonly firebaseConfig: NotificationFirebaseConfig
  readonly analyticsConfig: NotificationAnalyticsConfig
  readonly mfeUrl: string
}

export interface ImpersonationProfile {
  readonly userProfileId: string
  readonly firstName: string
  readonly lastName: string
  readonly companyName: string
  readonly email: string
  readonly label?: string
}

/**
 * This is the interface of our final export.
   - Extend this as needed with strongly typed values for the best support within your app.
   - TypeScript Interface docs https://www.typescriptlang.org/docs/handbook/interfaces.html
 */
export interface ApiConfig extends CommonConfig {
  readonly auth: AuthConfig // Overwrite the commonConfig. In this case making it more strict.
  readonly negotiator: { mfeSrc: string }
  readonly tile: { mfeSrc: string; boardsInWorkspaceMfeSrc: string }
  readonly spark: { mfeSrc: string; url: string }
  readonly notifications: NotificationConfig
  readonly impersonationProfiles: readonly ImpersonationProfile[]
}

// This is the type of keys that may be shared across environments.
type CommonConfig = {
  readonly xApiKey: string
  readonly auth: AuthConfig
  readonly tile: { mfeSrc: string; boardsInWorkspaceMfeSrc: string }
  readonly pol: {
    readonly auth: AuthConfig
    readonly negotiator: { mfeSrc: string }
    readonly spark: { mfeSrc: string; url: string }
  }
  readonly securityUrl: string
}

// Defines the environments we are building for.
// These must match the top-level keys of envConfigs.
//
export type ReactAppEnv = 'local' | 'dev' | 'stage' | 'prod'
type EnvConfigs = { readonly [key in ReactAppEnv]: ApiConfig }

// Keep these profiles limited to identity and display fields; impersonation
// uses the profile ID and resolves the current profile through token-service.
const devImpersonationProfiles: readonly ImpersonationProfile[] = [
  {
    userProfileId: 'e7744dee-6d75-4394-8ba1-09431fa3c59f',
    firstName: 'Brandon',
    lastName: 'Jenner',
    companyName: 'CDG',
    email: 'sparkdesignpartner@target.com',
    label: 'Design Partner',
  },
  // TODO: Review and potentially re-enable these test profiles for impersonation, they currently don't work
  // {
  //   userProfileId: '8dc3c5ee-0760-40ef-9877-9bbc925848fa',
  //   firstName: 'cypress2',
  //   lastName: 'external2',
  //   companyName: 'Renfro Hosiery Mills',
  //   email: 'spark-notifications@target.com',
  //   label: '[Non-accredited] External User',
  // },
  // {
  //   userProfileId: '8dc3c5ee-0760-40ef-9877-9bbc925848fa',
  //   firstName: 'cypress',
  //   lastName: 'external',
  //   companyName: 'Renfro Hosiery Mills',
  //   email: 'spark-notifications@target.com',
  //   label: '[Accredited] External User',
  // },
  {
    userProfileId: '6279fa7e-6ad9-455c-af13-1a2a556b61aa',
    firstName: 'Generic',
    lastName: 'Fabric Contact',
    companyName: 'Hansae Co. Ltd.',
    email: 'hansae.fabric@hansae.com',
  },
]

const stageImpersonationProfiles: readonly ImpersonationProfile[] = [
  ...devImpersonationProfiles,
]

const NOTIFICATION_MFE_SRC =
  'merchNotificationMFE@/mfe/notifications/remoteEntry.js'

// Configurations shared between most or all environments can be stored in commonConfig.
const commonConfig: CommonConfig = {
  xApiKey: 'fc09eb829153e1a9c2a8171dd9218bde8fbe4ee9',
  auth: {
    clientId: 'ob_plm_npe_im',
    authorizationUrl:
      'https://oauth.iam.perf.target.com/auth/oauth/v2/authorize',
    logoutUrl:
      'https://logonservices.iam.perf.target.com/login/responses/logoff.html',
  },
  tile: {
    mfeSrc: 'tile_container@/mfe/tile/remoteEntry.js',
    boardsInWorkspaceMfeSrc:
      'boardsInWorkspace@/mfe/tile/boardsInWorkspaceRemoteEntry.js',
  },
  pol: {
    auth: {
      authorizationUrl:
        'https://oauth.iam.perf.partnersonline.com/auth/oauth/v2/tgt/authorize/nla/1',
      logoutUrl:
        'https://oauth.iam.perf.partnersonline.com/login/responses/logoff.html',
      clientId: 'ob_plm_npe_im',
    },
    spark: {
      mfeSrc: 'spark_ui@http:/plm.perf.partnersonline.com/remoteEntry.js',
      url: 'https://plm.perf.partnersonline.com',
    },
    negotiator: {
      mfeSrc:
        'negotiator_ui@//plm.perf.partnersonline.com/mfe/negotiator/remoteEntry.js',
    },
  },
  securityUrl: '/api/security',
}

const devNotificationConfig: NotificationConfig = {
  apiConfig: {
    baseUrl: '/api/app_notifications/v1',
    appName: 'ownedbrands_notification',
    apiKey: '87d2b137e76d05d006c83da2db0b6071c8c8fa93',
    getUrl: 'notifications/in-app',
    statusUpdateUrl: 'notifications/status',
    deleteUrl: 'notifications/in-app',
    filterUrl: 'notifications/filters',
  },
  firebaseConfig: {
    vapidKey:
      'BPjUPg0txMqIymZwBwZsyNf_j0YqWEu8kCG2Z2Ins9VuvnL6EIbpOgoybKAetb1XuM2fIolu4K9Hr3w1qzql5q4',
    apiKey: 'AIzaSyDo_TL-WBBfYRikf_pxdaOZgm6ZAZ44Rzo',
    authDomain: 'tgt-ownedbrands-plm-firebase.firebaseapp.com',
    projectId: 'tgt-ownedbrands-plm-firebase',
    storageBucket: 'tgt-ownedbrands-plm-firebase.firebasestorage.app',
    messagingSenderId: '603300269495',
    appId: '1:603300269495:web:b00f9a5021f1103a4dac32',
  },
  analyticsConfig: {
    appName: 'ownedbrands_notification',
    apiKey: '87d2b137e76d05d006c83da2db0b6071c8c8fa93',
    url: 'https://plmnotifications-bff.dev.target.com/analytics',
  },
  mfeUrl: NOTIFICATION_MFE_SRC,
}

const baseConfigs: EnvConfigs = {
  local: {
    ...commonConfig,
    impersonationProfiles: devImpersonationProfiles,
    spark: {
      mfeSrc: 'spark_ui@//plm.dev.target.com/mfe/spark/remoteEntry.js',
      url: 'http://localhost:3000',
    },
    negotiator: {
      mfeSrc:
        'negotiator_ui@//plm.dev.target.com/mfe/negotiator/remoteEntry.js',
    },
    notifications: devNotificationConfig,
  },
  dev: {
    ...commonConfig,
    impersonationProfiles: devImpersonationProfiles,
    auth: { clientId: 'ob_plm_npe_im' },
    spark: {
      mfeSrc: 'spark_ui@//plm.dev.target.com/mfe/spark/remoteEntry.js',
      url: 'https://spark.dev.target.com',
    },
    negotiator: {
      mfeSrc:
        'negotiator_ui@//plm.dev.target.com/mfe/negotiator/remoteEntry.js',
    },
    notifications: devNotificationConfig,
  },
  stage: {
    ...commonConfig,
    impersonationProfiles: stageImpersonationProfiles,
    pol: {
      ...commonConfig.pol,
    },
    spark: {
      mfeSrc: 'spark_ui@//plm.perf.target.com/mfe/spark/remoteEntry.js',
      url: 'https://spark.perf.target.com/',
    },
    negotiator: {
      mfeSrc:
        'negotiator_ui@//plm.perf.target.com/mfe/negotiator/remoteEntry.js',
    },
    notifications: {
      apiConfig: {
        baseUrl: '/api/app_notifications/v1',
        appName: 'ownedbrands_notification',
        apiKey: '87d2b137e76d05d006c83da2db0b6071c8c8fa93',
        getUrl: 'notifications/in-app',
        statusUpdateUrl: 'notifications/status',
        deleteUrl: 'notifications/in-app',
        filterUrl: 'notifications/filters',
      },
      firebaseConfig: {
        vapidKey:
          'BPjUPg0txMqIymZwBwZsyNf_j0YqWEu8kCG2Z2Ins9VuvnL6EIbpOgoybKAetb1XuM2fIolu4K9Hr3w1qzql5q4',
        apiKey: 'AIzaSyDo_TL-WBBfYRikf_pxdaOZgm6ZAZ44Rzo',
        authDomain: 'tgt-ownedbrands-plm-firebase.firebaseapp.com',
        projectId: 'tgt-ownedbrands-plm-firebase',
        storageBucket: 'tgt-ownedbrands-plm-firebase.firebasestorage.app',
        messagingSenderId: '603300269495',
        appId: '1:603300269495:web:fa0e4dba2f8557274dac32',
      },
      analyticsConfig: {
        appName: 'ownedbrands_notification',
        apiKey: '87d2b137e76d05d006c83da2db0b6071c8c8fa93',
        url: 'https://plmnotifications-bff-stg.dev.target.com/analytics',
      },
      mfeUrl: NOTIFICATION_MFE_SRC,
    },
  },
  prod: {
    ...commonConfig,
    impersonationProfiles: [],
    xApiKey: '6cdbc038fa647578b12ad2990a75d8f293ec8e0e',
    auth: {
      authorizationUrl: 'https://oauth.iam.target.com/auth/oauth/v2/authorize',
      logoutUrl:
        'https://logonservices.iam.target.com/login/responses/logoff.html',
      clientId: 'ob_plm_prod_im',
    },
    spark: {
      mfeSrc: 'spark_ui@//plm.target.com/mfe/spark/remoteEntry.js',
      url: 'https://spark.target.com',
    },
    negotiator: {
      mfeSrc: 'negotiator_ui@//plm.target.com/mfe/negotiator/remoteEntry.js',
    },
    pol: {
      auth: {
        authorizationUrl:
          'https://oauth.iam.partnersonline.com/auth/oauth/v2/tgt/authorize/nla/1',
        logoutUrl:
          'https://oauth.iam.partnersonline.com/login/responses/logoff.html',
        clientId: 'ob_plm_prod_im',
      },
      spark: {
        mfeSrc: 'spark_ui@//spark.partnersonline.com/mfe/spark/remoteEntry.js',
        url: 'https://spark.partnersonline.com',
      },
      negotiator: {
        mfeSrc:
          'negotiator_ui@//plm.partnersonline.com/mfe/negotiator/remoteEntry.js',
      },
    },
    securityUrl: '/api/security',
    notifications: {
      apiConfig: {
        baseUrl: '/api/app_notifications/v1',
        appName: 'ownedbrands_notification',
        apiKey: 'f853b11e5693228793847ee07e6d66ccfad57baf',
        getUrl: 'notifications/in-app',
        statusUpdateUrl: 'notifications/status',
        deleteUrl: 'notifications/in-app',
        filterUrl: 'notifications/filters',
      },
      firebaseConfig: {
        vapidKey:
          'BPjUPg0txMqIymZwBwZsyNf_j0YqWEu8kCG2Z2Ins9VuvnL6EIbpOgoybKAetb1XuM2fIolu4K9Hr3w1qzql5q4',
        apiKey: 'AIzaSyDo_TL-WBBfYRikf_pxdaOZgm6ZAZ44Rzo',
        authDomain: 'tgt-ownedbrands-plm-firebase.firebaseapp.com',
        projectId: 'tgt-ownedbrands-plm-firebase',
        storageBucket: 'tgt-ownedbrands-plm-firebase.firebasestorage.app',
        messagingSenderId: '603300269495',
        appId: '1:603300269495:web:b8f7a7d1110188414dac32',
      },
      analyticsConfig: {
        appName: 'ownedbrands_notification',
        apiKey: 'f853b11e5693228793847ee07e6d66ccfad57baf',
        url: 'https://plmnotifications-bff.prod.target.com/analytics',
      },
      mfeUrl: NOTIFICATION_MFE_SRC,
    },
  },
}

// Only apply local overrides when running the dev server on a developer's
// machine (`praxis-scripts start`). `praxis-scripts build` sets NODE_ENV to
// 'production', so CI-built bundles will never include these overrides — do
// NOT gate this on a REACT_APP_* variable, since those can be baked in from
// a committed `.env` file and end up in production builds.
const localConfigOverrides =
  process.env.NODE_ENV === 'development'
    ? {
        local: {
          spark: {
            mfeSrc: 'spark_ui@//localhost:4000/mfe/spark/remoteEntry.js',
          },
        },
        dev: {
          spark: {
            mfeSrc: 'spark_ui@//localhost:4000/mfe/spark/remoteEntry.js',
          },
        },
      }
    : {}

export const envConfigs: EnvConfigs = _.merge(
  {},
  baseConfigs,
  localConfigOverrides,
)
